import { type JSX, useContext, useRef, useState } from "react";
import { WatchQueryFetchPolicy } from "@apollo/client";
import { captureMessage } from "@sentry/browser";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { IMAGE_MIME_TYPES, VIDEO_MIME_TYPES } from "common/constants";
import { buildLink, getPath, INFO_PANEL_TAB_NAME } from "common/routing";
import { getInfoPanelLinkForAttached } from "common-client/utils/customObject";
import { useGetDocumentUploadQuery } from "../../generated/graphql";
import useURLSearchParams from "../../hooks/useURLSearchParams";
import { setTitle } from "../../utils/title";
import { getCurrentURL } from "../../utils/url";
import { AuthContext } from "../Authorization/AuthContext";
import { BANNER_TYPES } from "../Common/__styles__/Banner";
import { FlexColumn } from "../Common/__styles__/Layout";
import Banner from "../Common/Banner";
import { Button } from "../Common/Buttons/Button";
import HeaderNav from "../Common/HeaderNav";
import { Body } from "../Common/Typography";
import AccessDeniedPage, {
  ACCESS_DENIED_RESOURCE,
} from "../Guest/AccessDeniedPage";
import LoadingSpinner from "../Submissions/ChatAgent/LoadingSpinner";
import {
  Container,
  Content,
  FileViewer,
  IFrame,
  Image,
  MissingLocationBannerContainer,
  MissingLocationEmphasis,
  Sidebar,
  Video,
  VideoFallbackContainer,
  VideoFallbackPlayer,
} from "./__styles__/DetailView";
import ActionButton from "./ActionButton";
import DetailViewContentHeader from "./DetailViewContentHeader";
import Tabs from "./Tabs";
import { useShowLocationTab } from "./useShowLocationTab";

export type DetailViewProps = {
  fetchPolicy?: WatchQueryFetchPolicy;
  generateUuid?: typeof uuidv4;
} & Partial<Pick<RouteComponentProps<{ id: string }>, "match">> & {
    documentUploadId?: string;
  };

// we allow for the overriding of fetch policy because `cache-and-network`
// does not appear to work in tests: https://github.com/apollographql/apollo-client/issues/6803
export const DetailView = ({
  match,
  fetchPolicy = "cache-and-network",
  documentUploadId,
  generateUuid = uuidv4,
}: DetailViewProps) => {
  const location = useLocation<{
    prevLocation: string;
    pathname: string;
    search: string;
  }>();
  const urlSearchParams = useURLSearchParams();

  const queryFetchPolicyParam = urlSearchParams.get("fetchPolicy");
  const validFetchPolicies: WatchQueryFetchPolicy[] = [
    "cache-and-network",
    "network-only",
  ];

  const isValidFetchPolicy = (
    policy: string | null,
  ): policy is WatchQueryFetchPolicy => {
    return (
      policy !== null &&
      validFetchPolicies.includes(policy as WatchQueryFetchPolicy)
    );
  };

  const queryFetchPolicy =
    isValidFetchPolicy(queryFetchPolicyParam) ? queryFetchPolicyParam : null;
  const dynamicFetchPolicy = queryFetchPolicy || fetchPolicy;

  const { account, isPublic } = useContext(AuthContext);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const documentUploadKey = useRef(generateUuid());

  const { data, error, loading, refetch } = useGetDocumentUploadQuery({
    variables: { id: documentUploadId ?? match!.params.id, isPublic }, // can remove documentUploadId when CertificateShim is totally removed
    fetchPolicy: dynamicFetchPolicy,
  });

  const showLocationTab = useShowLocationTab(data?.documentUpload);
  const [videoError, setVideoError] = useState(false);

  if (loading && !data?.documentUpload) return null;

  if (!data?.documentUpload || error)
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.FILE} />;

  const documentUpload = data.documentUpload;

  if (isPublic) {
    setTitle(`File | ${documentUpload.originalFilename}`);

    // For now, we only want to explose this view to ECs
    if (!documentUpload.certificateUpload) {
      captureMessage(`A guest attempted to view a non-EC in detail view`);
      return null;
    }
  }

  const mimeType = documentUpload.mimeType;

  const fileUrl = buildLink("documentUploadFile", { id: documentUpload.id });

  const actionButtonProps = {
    isDetailView: true,
    onUpdate: refetch,
    documentUpload,
    prevLocation: getCurrentURL(location),
  };

  const actionButton = <ActionButton {...actionButtonProps} />;

  const defaultPrevLocation =
    isPublic ?
      getPath("guestHome")
    : (getInfoPanelLinkForAttached({
        objectType: documentUpload.objectType,
        property: documentUpload.property,
        customMapGeometry:
          documentUpload.customMapGeometry ?
            {
              id: documentUpload.customMapGeometry.id,
              customMapId: documentUpload.customMapGeometry.customMapId,
            }
          : undefined,
        tab: INFO_PANEL_TAB_NAME.DOCUMENT_UPLOADS,
      }) ?? buildLink("documentUploads"));

  const prevLocation = location?.state?.prevLocation || defaultPrevLocation;

  const isVideo = VIDEO_MIME_TYPES.includes(mimeType);

  let viewDocumentComponent: JSX.Element;

  if (IMAGE_MIME_TYPES.includes(mimeType)) {
    viewDocumentComponent = <Image src={fileUrl} data-testid="document-img" />;
  } else if (isVideo) {
    if (videoError) {
      viewDocumentComponent = (
        <VideoFallback
          fileUrl={fileUrl}
          fileName={documentUpload.originalFilename}
        />
      );
    } else {
      viewDocumentComponent = (
        <Video
          src={fileUrl}
          controls
          data-testid="document-video"
          onError={() => setVideoError(true)}
        />
      );
    }
  } else {
    viewDocumentComponent = (
      <IFrame
        // editing a generated document will not re-render the iframe because the fileUrl doesn't change
        // using a unique documentUploadKey in the url will force a re-render of the iframe
        // we have to use a ref here and not state because changing state will force the parent
        // to re-render and then the iframe to re-render which looks like a duplicate re-render to the user
        src={`${fileUrl}?nonce=${documentUploadKey.current}`}
        data-testid="document-iframe"
      />
    );
  }

  const refetchAndSetKey = async () => {
    documentUploadKey.current = generateUuid();
    await refetch();
  };

  return (
    <Container data-testid="details-view">
      <HeaderNav
        subtitle={documentUpload.originalFilename}
        prevLocation={prevLocation}
        rightContainer={actionButton}
      />
      <Content>
        <Sidebar ref={sidebarRef}>
          <DetailViewContentHeader
            submission={documentUpload.submission}
            accountDocumentTypeName={documentUpload.accountDocumentType.name}
          />
          {showLocationTab && (
            <MissingLocationBannerContainer>
              <Banner type={BANNER_TYPES.ATTENTION}>
                <MissingLocationEmphasis>
                  This file isn't linked to a property.
                </MissingLocationEmphasis>{" "}
                Add the location to view it on your map and property list.
              </Banner>
            </MissingLocationBannerContainer>
          )}
          <div style={{ marginTop: "24px" }}>
            <Tabs
              documentUpload={documentUpload}
              sidebarRef={sidebarRef}
              account={account!}
              fileUrl={fileUrl}
              prevLocation={prevLocation}
              onUpdate={refetchAndSetKey}
            />
          </div>
        </Sidebar>
        <FileViewer>{viewDocumentComponent}</FileViewer>
      </Content>
    </Container>
  );
};

const VideoFallback = ({
  fileUrl,
  fileName,
}: {
  fileUrl: string;
  fileName: string;
}) => {
  const { t } = useTranslation();
  return (
    <VideoFallbackContainer data-testid="video-fallback">
      <VideoFallbackPlayer>
        <LoadingSpinner size="16px" />
        <Body size="small" type="regular" color="contentPrimaryInverted">
          {t("video-fallback-processing")}
        </Body>
      </VideoFallbackPlayer>
      <FlexColumn gap="m">
        <div>
          <Body size="large" type="emphasis" color="contentPrimary">
            {t("video-fallback-title")}
          </Body>
          <Body size="large" type="regular" color="contentSecondary">
            {t("video-fallback-subtitle")}
          </Body>
        </div>
        <a
          href={fileUrl}
          download={fileName}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Button
            title={t("video-fallback-download-button")}
            iconStart="download"
            size="medium"
            variant="secondary"
          />
        </a>
      </FlexColumn>
    </VideoFallbackContainer>
  );
};
